import { createAction } from '@reduxjs/toolkit'
import { isUndefined } from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { prepareActiveDrawableBeforeSubmit } from '../utils/prepareActiveDrawableBeforeSubmit'
import {
    fetchOpeningGroupPending,
    filterDrawableGroups,
    setActiveDrawable,
    setActiveDrawableGroup,
    updateOpeningGroupsSuccess,
} from '../../../actions/drawable'
import { UpdateOpeningGroupApiResponse } from '../../../api/api-helper'
import { updateOpeningGroup } from '../../../api/takeoff-api'
import { ActiveDrawable, OpeningGroup } from '../../../models/activeDrawable'
import { Project } from '../../../models/project'
import { Building } from '../../../models/projectBuilding'
import { Region } from '../../../models/region'
import IndexableObject from '../../../shared/constants/general-enums/indexableObject'
import { updateDrawableSelections } from '../../../shared/services/opening-form-services'
import { getUnitOfMeasureByDrawingType } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import Select from '../../lib/toolBoxes/2D/tools/select/Select.tool'
import {
    clearPendingRimboardBorders,
    createMultipleDrawableLocations,
    resetNewCreatedDrawables,
    selectRimboardPendingBorderIds,
    setFlag,
    unsetActiveDrawableId,
} from '../../slices/2D'
import { selectActiveBuildingFromActiveFloor } from '../../slices/buildings'
import { updateToolbarMessage } from '../../slices/common'
import { formError } from '../../slices/forms'
import { selectProject } from '../../slices/project'
import { selectAllRegions } from '../../slices/region'
import { activateTool } from '../../slices/tools'
import { DRAW_TOOL_ACTION, SAVING_MESSAGE } from '../../types'
import { bulkCreateDrawableLocations } from '../2D/bulkCreateDrawableLocations'

export const submitRimboardPerimeterForm = createAction<{ formData: OpeningGroup['settings'] }>(
    'submitRimboardPerimeterForm'
)

export function* applyPrepareDrawables(
    openings: ActiveDrawable[],
    settings: IndexableObject,
    configuration: string,
    remarks: string,
    prepareDrawables,
    scaleFactor: number,
    dpi: number | null,
    xCalibrationFactor: number,
    yCalibrationFactor: number,
    pdfScale: number,
    regions: Region[]
) {
    const baseOpenings = yield call(updateDrawableSelections, openings, { settings, configuration, remarks })

    if (isUndefined(prepareDrawables)) return baseOpenings

    return yield call(prepareDrawables, {
        drawables: baseOpenings,
        newSettings: settings,
        scaleFactor,
        dpi,
        xCalibrationFactor,
        yCalibrationFactor,
        pdfScale,
        regions,
    })
}

export function* handleRimboardPerimeterFormSubmit({ payload }: ReturnType<typeof submitRimboardPerimeterForm>) {
    const rimboardPendingBorderIds: number[] | null = yield select(selectRimboardPendingBorderIds)

    if (!rimboardPendingBorderIds?.length) return

    try {
        yield put(updateToolbarMessage(SAVING_MESSAGE))

        yield call(bulkCreateDrawableLocations, {
            payload: rimboardPendingBorderIds,
            type: createMultipleDrawableLocations.type,
        })

        const results = yield call(prepareActiveDrawableBeforeSubmit, { formData: payload.formData })
        const regions = yield select(selectAllRegions)

        if (!results) {
            yield put(clearPendingRimboardBorders())

            return
        }

        const {
            activeDrawableGroup,
            prepareDrawables,
            openings,
            settings,
            configuration,
            remarks,
            scaleFactor,
            dpi,
            xCalibrationFactor,
            yCalibrationFactor,
            pdfScale,
        } = results

        yield put(fetchOpeningGroupPending())

        const project: Project = yield select(selectProject)

        const preparedOpenings = yield call(
            applyPrepareDrawables,
            openings,
            settings,
            configuration,
            remarks,
            prepareDrawables,
            scaleFactor,
            dpi,
            xCalibrationFactor,
            yCalibrationFactor,
            pdfScale,
            regions
        )

        const activeBuilding: Building | undefined = yield select(selectActiveBuildingFromActiveFloor)
        const req = {
            openings: preparedOpenings,
            settings: {
                ...settings,
                unit_of_measure: getUnitOfMeasureByDrawingType(activeDrawableGroup.settings.shape_type),
                shape_type: activeDrawableGroup.settings.shape_type,
                rcm_building_id: activeBuilding?.rcm_id,
                bundle_name: settings.bundle_name,
            },
            type: activeDrawableGroup.type,
            configuration,
            remarks,
            isSave: true,
        }

        const res = yield call(updateOpeningGroup, activeDrawableGroup.project_id, req, String(activeDrawableGroup.id))

        const formattedResponse: UpdateOpeningGroupApiResponse = {
            newGroup: res,
            originalGroup: {
                id: activeDrawableGroup.id,
                project_id: activeDrawableGroup.project_id,
                color: null,
                ...req,
            },
        }

        yield put(updateOpeningGroupsSuccess(formattedResponse))

        yield all([
            put({ type: activateTool.type, payload: Select.NAME }),
            put(unsetActiveDrawableId()),
            put(setActiveDrawable(null, 0)),
            put(setActiveDrawableGroup(null)),
            put(filterDrawableGroups()),
            put(setFlag(DRAW_TOOL_ACTION.NEW_OPENING)),
            put(resetNewCreatedDrawables()),
            put(clearPendingRimboardBorders()),
        ])

        if (!project.is3D) {
            const manager: PaperManager | null = yield call(managers.get2DManager)

            if (!manager) return

            const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

            yield call(workspaceTool.lockItems, false)
        }
    } catch (error) {
        yield put(formError((error as any).message))
    } finally {
        yield put(updateToolbarMessage(null))
    }
}

export function* watchForRimboardPerimeterFormSubmit() {
    yield takeLatest(submitRimboardPerimeterForm.type, handleRimboardPerimeterFormSubmit)
}
